import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthShell from "@/pages/auth/components/AuthShell";
import { supabase, pathPrefix } from "@/lib/supabase";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [stage, setStage] = useState<"email" | "code" | "reset">("email");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const inputClass =
    "h-[52px] w-full rounded-[14px] border border-[#E4F5F5] bg-[#F0FDFD] px-4 text-sm text-ghasla-text-heading outline-none transition-colors focus:border-[#00AAB7]";
  const labelClass = "mb-2 block text-sm font-medium text-ghasla-text-primary";

  const handleSend = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const redirectTo = `${window.location.origin}${pathPrefix}/auth/callback?next=/auth/reset-password`;
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    setLoading(false);
    if (err) {
      setError("تعذر إرسال كود الاستعادة، تأكد من البريد الإلكتروني.");
    } else {
      setStage("code");
    }
  };

  const handleVerify = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    // كود تجريبي للمعاينة — يتخطى التحقق مباشرة
    if (code === "123456") {
      setStage("reset");
      return;
    }
    setLoading(true);
    const { error: err } = await supabase.auth.verifyOtp({
      email,
      token: code,
      type: "recovery",
    });
    setLoading(false);
    if (err) {
      setError("الكود غير صحيح أو منتهي.");
    } else {
      setStage("reset");
    }
  };

  const handleReset = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    // في وضع المعاينة (بدون session): نتخطى التحديث ونروح لتسجيل الدخول
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        navigate("/auth/login", { replace: true });
      }, 800);
      return;
    }
    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (err) {
      setError("تعذر تحديث كلمة المرور، حاول مرة أخرى.");
    } else {
      navigate("/auth/login", { replace: true });
    }
  };

  return (
    <AuthShell title="استعادة كلمة المرور" subtitle="بنرسل لك كود لإعادة تعيين كلمة المرور">
      {stage === "email" && (
        <form onSubmit={handleSend} className="space-y-5">
          <div>
            <label className={labelClass}>البريد الإلكتروني</label>
            <input
              type="email"
              className={inputClass}
              dir="ltr"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.sa"
              required
            />
          </div>
          {error && <p className="text-sm font-medium text-[#E74C3C]">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="h-[52px] w-full cursor-pointer rounded-[14px] bg-gradient-to-l from-[#00AAB7] to-[#00C4CF] text-base font-bold text-white shadow-glow transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {loading ? "جارٍ الإرسال..." : "إرسال كود الاستعادة"}
          </button>
          <p className="text-center text-sm text-ghasla-text-primary">
            <Link to="/auth/login" className="font-medium text-ghasla-text-link hover:underline">
              العودة لتسجيل الدخول
            </Link>
          </p>
        </form>
      )}

      {stage === "code" && (
        <form onSubmit={handleVerify} className="space-y-5">
          <div className="rounded-2xl bg-[#F0FDFD] p-4 text-sm text-ghasla-text-primary">
            أدخل الكود المرسل إلى <span className="font-semibold" dir="ltr">{email}</span>
          </div>
          <input
            className={`${inputClass} text-center text-lg tracking-[0.5em]`}
            dir="ltr"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="000000"
            inputMode="numeric"
            required
          />
          <p className="text-center text-xs text-ghasla-text-secondary">
            للتجربة: استخدمي الكود <span className="font-semibold" dir="ltr">123456</span>
          </p>
          {error && <p className="text-sm font-medium text-[#E74C3C]">{error}</p>}
          <button
            type="submit"
            disabled={loading || code.length !== 6}
            className="h-[52px] w-full cursor-pointer rounded-[14px] bg-gradient-to-l from-[#00AAB7] to-[#00C4CF] text-base font-bold text-white shadow-glow transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {loading ? "جارٍ التحقق..." : "تأكيد الكود"}
          </button>
        </form>
      )}

      {stage === "reset" && (
        <form onSubmit={handleReset} className="space-y-5">
          <div>
            <label className={labelClass}>كلمة المرور الجديدة</label>
            <input
              type="password"
              className={inputClass}
              dir="ltr"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="8 أحرف على الأقل"
              minLength={8}
              required
              autoComplete="new-password"
            />
          </div>
          {error && <p className="text-sm font-medium text-[#E74C3C]">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="h-[52px] w-full cursor-pointer rounded-[14px] bg-gradient-to-l from-[#00AAB7] to-[#00C4CF] text-base font-bold text-white shadow-glow transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {loading ? "جارٍ الحفظ..." : "حفظ كلمة المرور"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}